import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";

const AudioCtx = createContext(null);
export const useAudio = () => useContext(AudioCtx);

export function AudioProvider({ tracks, children }) {
  const audioRef = useRef(null);
  if (audioRef.current === null && typeof Audio !== "undefined") {
    audioRef.current = new Audio();
    audioRef.current.preload = "metadata";
    if (typeof window !== "undefined") window.__fre3brdAudio = audioRef.current;
  }

  const [currentIndex, setCurrentIndex] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffering, setBuffering] = useState(false);

  const currentIndexRef = useRef(currentIndex);
  currentIndexRef.current = currentIndex;

  const playIndex = useCallback(
    (index) => {
      const audio = audioRef.current;
      if (!audio || !tracks || !tracks[index]) return;

      if (index === currentIndexRef.current) {
        if (audio.paused) audio.play().catch(() => {});
        else audio.pause();
        return;
      }
      audio.src = tracks[index].src;
      audio.currentTime = 0;
      setCurrentIndex(index);
      setBuffering(true);
      audio.play().catch(() => {});
    },
    [tracks]
  );

  const toggle = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (currentIndexRef.current === -1) {
      playIndex(0);
      return;
    }
    if (audio.paused) audio.play().catch(() => {});
    else audio.pause();
  }, [playIndex]);

  const next = useCallback(() => {
    if (!tracks || !tracks.length) return;
    const n = (currentIndexRef.current + 1) % tracks.length;
    playIndex(n);
  }, [tracks, playIndex]);

  const prev = useCallback(() => {
    const audio = audioRef.current;
    if (!tracks || !tracks.length || !audio) return;
    if (audio.currentTime > 3) {
      audio.currentTime = 0;
      return;
    }
    const p = (currentIndexRef.current - 1 + tracks.length) % tracks.length;
    playIndex(p);
  }, [tracks, playIndex]);

  const seek = useCallback((ratio) => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    audio.currentTime = ratio * audio.duration;
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTime = () => {
      setCurrentTime(audio.currentTime);
      setDuration(audio.duration || 0);
      setProgress(audio.duration ? audio.currentTime / audio.duration : 0);
    };
    const onMeta = () => setDuration(audio.duration || 0);
    const onPlay = () => {
      setIsPlaying(true);
      setBuffering(false);
    };
    const onPause = () => setIsPlaying(false);
    const onWaiting = () => setBuffering(true);
    const onPlaying = () => setBuffering(false);
    const onEnded = () => {
      const n = (currentIndexRef.current + 1) % tracks.length;
      playIndex(n);
    };

    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("waiting", onWaiting);
    audio.addEventListener("playing", onPlaying);
    audio.addEventListener("ended", onEnded);

    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("waiting", onWaiting);
      audio.removeEventListener("playing", onPlaying);
      audio.removeEventListener("ended", onEnded);
    };
  }, [tracks, playIndex]);

  const value = {
    tracks,
    currentIndex,
    currentTrack: currentIndex >= 0 ? tracks[currentIndex] : null,
    isPlaying,
    progress,
    currentTime,
    duration,
    buffering,
    playIndex,
    toggle,
    next,
    prev,
    seek,
  };

  return <AudioCtx.Provider value={value}>{children}</AudioCtx.Provider>;
}
